import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { collateralApi, ApiError } from '../services/collateralApi';

export default function useDraweeConfirmationPageList() {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [showNewForm, setShowNewForm] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [priorityFilter, setPriorityFilter] = useState<'' | 'true' | 'false'>('');
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });
    const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
    const [priorityCounts, setPriorityCounts] = useState<{ priority: number; notPriority: number }>({ priority: 0, notPriority: 0 });
    const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
    const pageSize = 100;

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await collateralApi.getDraweeConfirmations({
                page, pageSize,
                status: statusFilter || undefined,
                search: search || undefined,
                priority: priorityFilter || undefined,
                sortField: sortField || undefined,
                sortOrder: sortOrder || undefined,
            });
            setRows(result.items || []);
            setTotal(result.total || 0);
            setApiInfo({ version: result.apiVersion || '', environment: result.environment || '' });
            setStatusCounts(result.statusCounts || {});
            setPriorityCounts(result.priorityCounts || { priority: 0, notPriority: 0 });
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, statusFilter, search, priorityFilter, sortField, sortOrder, navigate]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    const refresh = () => setRefreshKey(k => k + 1);

    const handleRowClick = (row: any) => {
        navigate(`/collateral/drawee-confirmation/${row.id}/receivables`);
    };

    const handlePriorityToggle = (e: React.MouseEvent, row: any) => {
        e.stopPropagation();
        if (togglingIds.has(row.id)) return;
        const newPriority = !row.priority;
        setTogglingIds(prev => new Set(prev).add(row.id));
        const shouldRemove = (priorityFilter === 'true' && !newPriority) || (priorityFilter === 'false' && newPriority);
        setRows(prev => shouldRemove
            ? prev.filter(r => r.id !== row.id)
            : prev.map(r => r.id === row.id ? { ...r, priority: newPriority } : r));
        if (shouldRemove) setTotal(t => Math.max(0, t - 1));
        setPriorityCounts(prev => ({
            priority: prev.priority + (newPriority ? 1 : -1),
            notPriority: prev.notPriority + (newPriority ? -1 : 1),
        }));
        collateralApi.updateVerificationConfig(row.id, { priority: newPriority })
            .catch(() => { refresh(); toast.error('Erro ao atualizar prioridade'); })
            .finally(() => setTogglingIds(prev => { const next = new Set(prev); next.delete(row.id); return next; }));
    };

    return {
        rows, total, page, setPage, pageSize,
        isLoading, initialLoading,
        apiInfo, statusCounts, priorityCounts, togglingIds,
        showNewForm, setShowNewForm,
        statusFilter, setStatusFilter,
        priorityFilter, setPriorityFilter,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        refresh, handleRowClick, handlePriorityToggle,
    };
}
